/**
 * Land Survey（土地测绘）可选块 —— 只挂在「Site Plan + Floor Plan 方案」（schematic）阶段。
 *
 * 需求来源（2026-10-20 用户原话，配合方案设计阶段卡截图的红色标注框）：
 *   「增加 land survey 的可选，有的是 2 周、有的是 1 个月」
 *   追问后明确：
 *     · 「可能不是两周或者一个月，这里可能要放宽一点」→ 不做固定档位
 *     · 「直接填天数」                              → 项目里自己填
 *     · 「单独显示在下面的前期先放宽一点，然后让他们自己能够配，他们就是三个负责人」
 *                                                   → 单独成块，三个项目负责人可改
 *     · 「就加在之前的全流程责任里面」               → 责任人配置并入产物与流程设置
 *     · 「这里因为也会涉及到时间延误的问题，也需要在这个阶段完成将时间顺延」
 *                                                   → 需要登记测绘时顺延项目工期
 *   第二轮（跟进待办）：
 *     · 「这个负责人需要没隔三天有一个待办」
 *     · 「待办需要填写是否跟进土地测绘进度..跟进内容是什么」
 *     · 「可选上传附件证件提交后待办消失」
 *     · 「进入到下一个三天进行后通知待办」
 *     · 「直到在项目中标记土地测绘完成并上传完成证明」
 *     · 「这个天数不按照设置的来..按照实际土地测绘填写到项目中完成来算延期时间」
 *     → 块里新增「跟进」区：待办到点显示提交面板（是/否 + 内容 + 可选附件）；
 *       已提交的记录按轮次列出；标记完成时显示实际用时与按实际天数的结算结果。
 *   第三轮（用户看到界面后否定「预计天数」输入框）：
 *     · 「这个 14 天不是说了不要了吗……只要实际交付时间就行……
 *        按照实际交付时间顺延，然后每三天待办让负责人去跟进」
 *     → **取消「预计天数」字段**：登记时不填天数、也不动工期；
 *       只有点「标记完成」时才按 `完成日 − 登记日` 的实际天数顺延项目。
 *       所以本文件里不再有 days 输入、改天数、预计交报告日这些元素。
 *
 * 为什么单独成块（而不是做成第 14 项产物）：
 *   1. 它不是每单都做 —— 设计部 13 项产物是「每单都要交」的口径，加进去会让所有
 *      项目的完成度被一项可选工作拖住（progress_of 按产物数量平均加权）；
 *   2. 它的产物形态不是图纸，而是第三方测绘公司的报告 + 一段等待期，和产物卡
 *      「状态 + 版本文件」的语义不同；
 *   3. 现有「本单不做」机制（product-skip / exempt）是为产物设计的，套到测绘上
 *      会污染产物统计口径。
 *
 * 三种状态对应三种界面（见 state）：
 *   idle    未登记 → 问「本项目需要 Land Survey 吗？」两个按钮
 *   waiting 已登记 → 显示已等待天数 / 负责人 / 跟进区 + 上传报告
 *   done    已完成 → 折叠成一行结论 + 实际用时 / 顺延天数 + 报告列表（保留历史）
 *   另：needed=false（已标记不需要）→ 只留一行「已标记不需要」+ 撤销入口
 *
 * 权限：写操作要求 meta.canEdit。用户说「让他们自己能够配」的三个负责人，
 * 在 can_edit 的判定里本来就包含项目三岗（owner / cdDesigner / usLead / submissionOwner），
 * 所以设计师不用找主管也能登记 / 提交跟进。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle, BellRing, CalendarClock, CheckCircle2, ClipboardList, Download,
  FileUp, History, Loader2, Lock, Map, Paperclip, RotateCcw, XCircle,
} from "lucide-react";
import { toast } from "../../../components/ui/sonner";
import { MediaLightbox, mediaKind } from "../../../components/MediaLightbox";
import { downloadFile } from "../../../lib/fileDownload";
import { DESIGN_UPLOAD_ACCEPT } from "./meta";
import { T } from "../../../lib/i18n";
import { fmtDT } from "../../../lib/usDate";
import { errTextOf } from "../../../lib/detailText";
import FileUploadDate, { uploadDateLabel } from "./FileUploadDate";

const fmtMB = (n) => `${((n || 0) / 1048576).toFixed(1)} MB`;

/* 主区块。按 pid 懒加载：只有展开方案设计阶段才请求，不拖慢项目列表。 */
export const LandSurveyBlock = ({ pid, stageKey, meta, api, onChanged }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState("");
  const [lb, setLb] = useState(null);
  const [lbItems, setLbItems] = useState([]);
  const inp = useRef(null);
  // 跟进表单：是否跟进（null = 还没选）+ 跟进内容 + 可选附件
  const [fuDid, setFuDid] = useState(null);
  const [fuText, setFuText] = useState("");
  const [fuFiles, setFuFiles] = useState([]);
  const fuInp = useRef(null);

  const load = useCallback(() => {
    setErr(null);
    return api.get(`/design/v3/projects/${pid}/land-survey`)
      .then(({ data }) => setD(data))
      .catch((e) => setErr(e?.detailText || e.message));
  }, [api, pid]);
  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); onChanged?.(); };

  /* 登记「需要」：**不填天数、不动工期**，只开始计时 + 起算跟进待办。
     用户看到界面后明确否掉了预计天数输入框（「这个 14 天不是说了不要了吗」），
     工期只在「标记完成」时按实际交付天数顺延，所以这里不再传 days。 */
  const enable = async () => {
    setBusy("enable");
    try {
      const { data } = await api.post(`/design/v3/projects/${pid}/land-survey`,
        { needed: true, note: note.trim() || undefined });
      toast.success(T("已登记 Land Survey：每 {0} 天一条跟进待办，工期在标记完成时按实际交付天数顺延", [
        data.landSurvey?.followupDays || 3]));
      setNote("");
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  /* 标记不需要：若之前顺延过，后端会把顺延撤回（工期退回），并停掉跟进循环。
     原因可选：不填也要能让用户标记（「本项目不需要」本身是有效结论，
     强行要理由会把人卡住，反而导致大家不登记）。 */
  const disable = async () => {
    if (!window.confirm(T("确认本项目不需要 Land Survey？\n\n如果之前已登记并顺延过工期，会一并撤回顺延，未完成的跟进待办也会关闭。"))) return;
    setBusy("disable");
    try {
      const { data } = await api.post(`/design/v3/projects/${pid}/land-survey`,
        { needed: false });
      toast.success(data.undo ? T("已标记不需要，并撤回 {0} 天顺延", [data.undo.days]) : T("已标记本项目不需要 Land Survey"));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  /* 改判回「需要」：从「不需要」恢复成等待测绘（重新起算计时与跟进循环）。
     不再涉及天数：登记口径已经改成「只开始计时」，所以这里直接置 needed=true 即可。 */
  const reopen = async () => {
    setBusy("reopen");
    try {
      await api.post(`/design/v3/projects/${pid}/land-survey`, { needed: true });
      toast.success(T("已改回「需要」，开始计时并生成跟进待办"));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", "");
      await api.post(`/design/v3/projects/${pid}/land-survey/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("已上传 {0}", [file.name]));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const voidFile = async (f) => {
    const why = (window.prompt(T("作废「{0}」的原因（会保留留档）", [f.name]), "") || "").trim();
    if (!why) return;
    setBusy(`${f.aid}-void`);
    try {
      await api.post(`/design/v3/projects/${pid}/land-survey/void`, { aid: f.aid, reason: why });
      toast.success(T("已作废，历史仍留档"));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  /* 提交跟进待办：必须选「是否跟进」并写清内容；附件可选（用户原话「可选上传附件证件」）。
     提交后这条待办在「今日代办」里消失，后端把下次跟进日推到 N 天后。 */
  const submitFollowup = async () => {
    if (fuDid === null) return toast.error(T("请选择是否跟进了测绘进度"));
    if (fuText.trim().length < 2) return toast.error(T("请填写跟进内容（≥2 字，没跟进也要写原因）"));
    setBusy("followup");
    try {
      const fd = new FormData();
      fd.append("followed", fuDid ? "1" : "0");
      fd.append("content", fuText.trim());
      (fuFiles || []).forEach((f) => fd.append("files", f));
      const { data } = await api.post(
        `/design/v3/projects/${pid}/land-survey/followup/${d.followupOpen}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("跟进已提交，下次跟进 {0} 天后（{1}）", [
        data.landSurvey?.followupDays || 3, data.landSurvey?.followupNextAt || "-"]));
      setFuDid(null); setFuText(""); setFuFiles([]);
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const markDone = async () => {
    setBusy("done");
    try {
      const { data } = await api.post(`/design/v3/projects/${pid}/land-survey/done`, {});
      const sd = data.shift?.days || 0;
      toast.success(T("Land Survey 已完成：实际用时 {0} 天{1}", [data.actualDays || 0,
        sd > 0 ? T("，项目按实际交付顺延 {0} 天", [sd]) :
          sd < 0 ? T("，项目回退 {0} 天", [-sd]) : T("，当天交付，工期无需顺延")]));
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const preview = (f) => {
    const items = (d?.files || []).filter((x) => !x.voided).map((x) => ({
      id: x.aid, fileId: x.fileId, size: x.size,
      url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${x.fileId}/download`,
      kind: mediaKind(x.name, x.contentType), name: x.name,
      meta: `${fmtMB(x.size)} · ${x.uploader} · ${uploadDateLabel(x.uploadedAt, x.at)}`,
      caption: x.note || "",
    }));
    const idx = items.findIndex((x) => x.id === f.aid);
    setLbItems(items);
    setLb(idx >= 0 ? idx : 0);
  };

  if (err) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50/50 px-3 py-2 text-[11px] text-red-700" data-testid={`dv3-ls-error-${stageKey}`}>
        <AlertTriangle className="mr-1 inline h-3 w-3" />{errTextOf(err)}
      </div>
    );
  }
  if (!d) {
    return <div className="py-2 text-center text-slate-400" data-testid={`dv3-ls-loading-${stageKey}`}><Loader2 className="h-3.5 w-3.5 animate-spin inline" /></div>;
  }

  const files = d.files || [];
  const live = files.filter((f) => !f.voided);
  const state = d.state || "idle";
  const canWrite = !!meta.canEdit;
  const followups = d.followups || [];
  const fuDays = d.followupDays || 3;
  const openFu = d.followupOpen || "";
  const lastFu = followups[followups.length - 1];
  /* 阶段顺序锁（2026-10-22 用户需求）：
     「需要开始应该是上个阶段完成才能点击..详情里好像没没限制..因为这个涉及到向后推迟时间」。

     修改原因：原先这块只校验写权限，只要页面能滚到这里，不管流程走到哪，
     「需要 · 开始计时」都能点 —— 一登记就开始计时，标记完成时还要按实际交付天数
     顺延项目工期，等于在流程没走到这一步时就先把时间推后。

     判定口径：与「完成该阶段」按钮和 stage_close_core 同一份比较 ——
     本阶段 order 排在项目 currentStage 之后（流程还没走到）才锁。

     最终效果：锁定按钮置灰，并直接写明流程现在停在哪一步，
     让人知道是「流程没轮到」而不是「权限不够」；后端同一判定会兜住直接调接口。

     为什么锁只作用于「登记需要」：已登记后改备注 / 换负责人不会重复起算，也就不会动工期，
     没必要拦；标记「不需要」是收回承诺、还会把已顺延的天数退回去，任何时候都该放行。 */
  const stageLock = d.stageLock || { allowed: true };
  const lockOn = stageLock.allowed === false;
  /* 文案说明「流程现在停在哪一步」而不是「上一阶段没做完」：
     判定看的是当前阶段，说成「上一阶段」会把还在并行的阶段 / 历史未补的阶段
     说反，用户按提示去找反而找不到卡点。 */
  const lockText = lockOn
    ? T("流程还没走到本阶段（当前停在「{0}」），现在还不能开始计时", [stageLock.currentName || stageLock.currentKey || ""])
    : "";

  /* 已标记「不需要」：只留一行结论 + 撤销入口，不占版面。 */
  if (d.needed === false) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-1.5" data-testid={`dv3-ls-${stageKey}`} data-state="off" data-needed="0">
        <Map className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{T("Land Survey（土地测绘）")}</span>
        <span className="text-[10px] font-semibold text-slate-500" data-testid={`dv3-ls-off-${stageKey}`}>
          {T("本项目不需要")}{d.skipReason ? `：${d.skipReason}` : ""}
        </span>
        {canWrite && (
          <button type="button" onClick={reopen} disabled={!!busy}
            data-testid={`dv3-ls-toggle-${stageKey}`}
            className="ml-auto inline-flex items-center gap-0.5 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-700 disabled:opacity-50">
            {busy === "reopen" ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RotateCcw className="h-2.5 w-2.5" />}{T("改为需要")}
          </button>
        )}
      </div>
    );
  }

  return (
    /* flex-1 + flex-col（2026-10-21 等高）：与任务卡并排时本块会被拉高，
       靠 flex-1 撑满外层单元格，边框才能和左侧任务卡底边对齐。
       未登记态（idle）本身的提示文字不多，用 mt-auto 把它贴到底部不现实，
       所以只让容器撑满、内容按原有顺序从上往下排。 */
    <div className={`flex flex-1 flex-col rounded-lg border p-2.5 space-y-2 ${state === "done" ? "border-emerald-200 bg-emerald-50/20" : "border-sky-200 bg-sky-50/30"}`}
      data-testid={`dv3-ls-${stageKey}`} data-state={state} data-needed={d.needed === true ? "1" : "0"}
      data-followup-open={openFu ? "1" : "0"}>
      <div className="flex flex-wrap items-center gap-2">
        <Map className="h-3.5 w-3.5 shrink-0 text-sky-600" />
        <span className="text-[11px] font-bold uppercase tracking-wide text-sky-800">{T("Land Survey（土地测绘）")}</span>
        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-500">{T("可选")}</span>
        <span className="text-[10px] text-slate-500" data-testid={`dv3-ls-count-${stageKey}`}>
          {state === "done" ? T("已完成") : state === "waiting" ? T("等测绘报告") : T("尚未登记")}
        </span>
        {d.owner && <span className="text-[10px] text-slate-400" data-testid={`dv3-ls-owner-${stageKey}`}>{T("负责 · {0}", [d.owner.name])}</span>}
        {state === "waiting" && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold text-violet-700"
            data-testid={`dv3-ls-fu-chip-${stageKey}`}>
            <BellRing className="h-2.5 w-2.5" />{T("每 {0} 天跟进", [fuDays])}{followups.length ? T(" · 已跟进 {0} 次", [followups.length]) : ""}
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400">{T("工期按实际交付天数顺延")}</span>
      </div>

      {/* 未登记：问要不要做。用户要求「可选」，所以默认不帮项目做决定。 */}
      {state === "idle" && (
        <div className="space-y-1.5 rounded-md border border-slate-200 bg-white px-2 py-2" data-testid={`dv3-ls-ask-${stageKey}`}>
          <p className="text-[11px] font-semibold text-slate-700">{T("本项目需要做 Land Survey（土地测绘）吗？")}</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-0 flex-1 text-[10px] text-slate-600">{T("备注（可选）")}
              <input value={note} onChange={(e) => setNote(e.target.value)} disabled={!canWrite}
                placeholder={T("例：测绘公司名 / 约定交报告日期")} data-testid={`dv3-ls-note-${stageKey}`}
                className="ml-1 w-full rounded-md border border-slate-200 px-1.5 py-0.5 text-[11px]" />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {canWrite && (
              <button type="button" onClick={enable} disabled={busy === "enable" || lockOn}
                title={lockOn ? lockText : T("登记需要测绘，开始计时并每 {0} 天生成一条跟进待办", [fuDays])}
                data-testid={`dv3-ls-enable-${stageKey}`} data-locked={lockOn ? "1" : "0"}
                className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {busy === "enable" ? <Loader2 className="h-3 w-3 animate-spin" /> : lockOn ? <Lock className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}{T("需要 · 开始计时")}
              </button>
            )}
            {canWrite && (
              <button type="button" onClick={disable} disabled={!!busy} data-testid={`dv3-ls-skip-${stageKey}`}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-slate-400 disabled:opacity-50">
                <XCircle className="h-3 w-3" />{T("本项目不需要")}
              </button>
            )}
            {!canWrite && <span className="text-[10px] text-slate-400">{T("仅项目负责人可登记")}</span>}
          </div>
          {/* 锁定时把「卡在哪一步」写明白：只置灰不说原因，用户会以为是权限或 bug。
              用 amber 而不是 red —— 这不是出错，只是流程还没轮到本阶段。 */}
          {lockOn && canWrite && (
            <p className="flex items-start gap-1 text-[10px] font-semibold text-amber-700" data-testid={`dv3-ls-lock-${stageKey}`}>
              <Lock className="mt-px h-2.5 w-2.5 shrink-0" />
              <span className="min-w-0">{lockText}</span>
            </p>
          )}
          <p className="text-[10px] text-slate-400">
            {T("登记后不改工期；负责人每 {0} 天收到一条跟进待办，最终在标记完成时按实际交付天数顺延项目。", [fuDays])}
          </p>
        </div>
      )}

      {/* 已登记：已等待天数 + 顺延结果（顺延只会在完成时出现） */}
      {state !== "idle" && (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-600" data-testid={`dv3-ls-meta-${stageKey}`}>
            {d.shiftedDays > 0 && (
              <span className="font-semibold text-amber-700" data-testid={`dv3-ls-shift-${stageKey}`}>
                {T("项目已按实际交付顺延 {0} 天", [d.shiftedDays])}{d.shift?.targetTo ? T("（目标完成日 → {0}）", [d.shift.targetTo]) : ""}
              </span>
            )}
            <span className="text-slate-500" data-testid={`dv3-ls-elapsed-${stageKey}`}>
              {d.state === "done" ? T("实际用时 {0} 天", [d.settledDays ?? d.elapsedDays]) : T("已等待 {0} 天", [d.elapsedDays])}
            </span>
            {d.startedAt && <span className="text-slate-400">{T("登记 {0} · {1}", [d.startedBy, fmtDT(d.startedAt)])}</span>}
            {d.note && <span className="truncate text-slate-500" title={d.note}>{d.note}</span>}
          </div>

          {/* 跟进区（2026-10-20 第二轮需求）：
              有未完成待办 → 显示提交面板；否则显示下次跟进日 + 历史记录。 */}
          {state === "waiting" && (
            <div className="rounded-md border border-violet-200 bg-white px-2 py-1.5 space-y-1.5"
              data-testid={`dv3-ls-fu-${stageKey}`} data-open={openFu ? "1" : "0"}>
              <div className="flex flex-wrap items-center gap-1.5">
                <BellRing className={`h-3 w-3 shrink-0 ${openFu ? "text-violet-600" : "text-slate-400"}`} />
                <span className="text-[11px] font-semibold text-slate-700">{T("进度跟进")}</span>
                {openFu ? (
                  <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold text-red-700" data-testid={`dv3-ls-fu-due-${stageKey}`}>
                    {T("待填写")}
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-500" data-testid={`dv3-ls-fu-next-${stageKey}`}>
                    {d.followupNextAt ? T("下次跟进 {0}", [d.followupNextAt]) : T("每 {0} 天自动生成", [fuDays])}
                  </span>
                )}
                {lastFu && (
                  <span className="ml-auto text-[10px] text-slate-400" data-testid={`dv3-ls-fu-last-${stageKey}`}>
                    {T("上次：{0} · {1}", [lastFu.followed ? T("已跟进") : T("未跟进"), fmtDT(lastFu.at)])}
                  </span>
                )}
              </div>

              {openFu && (
                <div className="space-y-1.5 rounded-md border border-violet-200 bg-violet-50/40 p-2"
                  data-testid={`dv3-ls-fu-form-${stageKey}`}>
                  <p className="text-[10px] font-semibold text-slate-700">{T("请填写本次跟进结果")}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] text-slate-600">{T("是否跟进")}</span>
                    {[[true, T("是")], [false, T("否")]].map(([v, label]) => (
                      <button key={String(v)} type="button" onClick={() => setFuDid(v)} disabled={!!busy}
                        data-testid={`dv3-ls-fu-did-${v ? "yes" : "no"}-${stageKey}`} data-on={fuDid === v ? "1" : "0"}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold disabled:opacity-50 ${fuDid === v ? (v ? "bg-emerald-600 text-white" : "bg-amber-500 text-white") : "border border-slate-200 bg-white text-slate-600 hover:border-violet-300"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <textarea value={fuText} onChange={(e) => setFuText(e.target.value)} disabled={!!busy}
                    placeholder={T("跟进内容（例：已催测绘公司，回复下周二出报告；没跟进请写原因）")}
                    data-testid={`dv3-ls-fu-text-${stageKey}`}
                    className="h-14 w-full rounded-md border border-slate-200 px-1.5 py-1 text-[11px]" />
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button type="button" onClick={() => fuInp.current?.click()} disabled={!!busy}
                      data-testid={`dv3-ls-fu-pick-${stageKey}`}
                      className="inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-violet-300 disabled:opacity-50">
                      <Paperclip className="h-2.5 w-2.5" />{T("附件（可选）")}
                    </button>
                    <input ref={fuInp} type="file" multiple className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
                      data-testid={`dv3-ls-fu-input-${stageKey}`}
                      onChange={(e) => { setFuFiles(Array.from(e.target.files || [])); }} />
                    <span className="min-w-0 flex-1 truncate text-[10px] text-slate-400" data-testid={`dv3-ls-fu-picked-${stageKey}`}>
                      {fuFiles.length ? fuFiles.map((f) => f.name).join("、") : T("无需上传也可提交")}
                    </span>
                    <button type="button" onClick={submitFollowup} disabled={!!busy}
                      data-testid={`dv3-ls-fu-submit-${stageKey}`}
                      className="inline-flex items-center gap-0.5 rounded-md bg-violet-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                      {busy === "followup" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}{T("提交跟进")}
                    </button>
                  </div>
                </div>
              )}

              {followups.length > 0 && (
                <ul className="space-y-0.5" data-testid={`dv3-ls-fu-list-${stageKey}`}>
                  {followups.slice().reverse().slice(0, 5).map((f) => (
                    <li key={f.tid} className="flex flex-wrap items-start gap-1 text-[10px]"
                      data-testid={`dv3-ls-fu-row-${f.tid}`} data-followed={f.followed ? "1" : "0"}>
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${f.followed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {f.followed ? T("已跟进") : T("未跟进")}
                      </span>
                      <span className="shrink-0 text-slate-400">{T("跟进时间：{0}", [fmtDT(f.at)])}{f.byName ? ` · ${f.byName}` : ""}</span>
                      <span className="min-w-0 flex-1 text-slate-700">{f.content}</span>
                      {(f.files || []).length > 0 && (
                        <span className="inline-flex shrink-0 flex-wrap items-center gap-0.5 text-violet-700">
                          <Paperclip className="h-2.5 w-2.5" />{(f.files || []).map((x) => (
                            <span key={x.fileId} className="inline-flex flex-wrap items-center gap-x-1">
                              <button type="button" title={T("下载")}
                                onClick={() => downloadFile(x.fileId, x.name).catch((e) => toast.error(e?.message || T("下载失败")))}
                                className="max-w-[90px] truncate underline decoration-dotted hover:text-violet-900">{x.name}</button>
                              <FileUploadDate value={x.uploadedAt} />
                            </span>
                          ))}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {followups.length > 5 && (
                <p className="text-[9px] text-slate-400" data-testid={`dv3-ls-fu-more-${stageKey}`}>
                  <History className="mr-0.5 inline h-2.5 w-2.5" />{T("仅显示最近 5 条，共 {0} 条", [followups.length])}
                </p>
              )}
            </div>
          )}

          {/* 报告上传：这是这项工作的交付物，缺它就不能标记完成 */}
          <div className="rounded-md border border-slate-200 bg-white px-2 py-1.5" data-testid={`dv3-ls-report-${stageKey}`} data-have={live.length ? "1" : "0"}>
            <div className="flex items-center gap-1.5">
              <ClipboardList className={`h-3 w-3 shrink-0 ${live.length ? "text-slate-400" : "text-red-500"}`} />
              <span className={`text-[11px] font-semibold ${live.length ? "text-slate-700" : "text-red-600"}`}>{T("测绘报告")}</span>
              {!live.length && <span className="text-[10px] font-semibold text-red-500">{T("未上传")}</span>}
              {canWrite && state !== "done" && (
                <>
                  <button type="button" onClick={() => inp.current?.click()} disabled={busy === "upload"}
                    data-testid={`dv3-ls-upload-${stageKey}`}
                    className="ml-auto inline-flex items-center gap-0.5 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-sky-300 hover:text-sky-700 disabled:opacity-50">
                    {busy === "upload" ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <FileUp className="h-2.5 w-2.5" />}{T("上传")}
                  </button>
                  <input ref={inp} type="file" className="hidden" accept={DESIGN_UPLOAD_ACCEPT}
                    data-testid={`dv3-ls-upload-input-${stageKey}`}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
                </>
              )}
            </div>
            {files.length > 0 && (
              <ul className="mt-1 space-y-0.5 pl-4" data-testid={`dv3-ls-files-${stageKey}`}>
                {files.map((f) => (
                  <li key={f.aid} className="flex items-center gap-1 text-[10px]" data-testid={`dv3-ls-file-${f.aid}`} data-voided={f.voided ? "1" : "0"}>
                    <div className="min-w-0 flex-1 basis-[180px]">
                      <button type="button" onClick={() => preview(f)} title={T("页内预览")}
                        className={`block w-full truncate text-left font-semibold ${f.voided ? "text-slate-400 line-through" : "text-sky-700 hover:underline"}`}>
                        {f.name}
                      </button>
                      <FileUploadDate value={f.uploadedAt} fallbackValue={f.at} className="block" />
                    </div>
                    <span className="shrink-0 text-slate-400">{fmtMB(f.size)}</span>
                    <button type="button" title={T("下载")} onClick={() => downloadFile(f.fileId, f.name).catch((e) => toast.error(e?.message || T("下载失败")))}
                      className="shrink-0 text-slate-400 hover:text-sky-700"><Download className="h-2.5 w-2.5" /></button>
                    {canWrite && !f.voided && state !== "done" && (
                      <button type="button" title={T("作废（保留留档）")} data-testid={`dv3-ls-void-${f.aid}`} onClick={() => voidFile(f)}
                        className="shrink-0 text-slate-400 hover:text-red-600"><XCircle className="h-2.5 w-2.5" /></button>
                    )}
                    {f.voided && <span className="shrink-0 text-red-500" title={f.voidReason || ""}>{T("已作废")}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* 操作行：标记完成（唯一改工期的动作） */}
          {state !== "done" && (
            <div className="flex flex-wrap items-center gap-2" data-testid={`dv3-ls-actions-${stageKey}`}>
              {canWrite && (
                <>
                  <span className="text-[9px] text-slate-400">
                    {T("标记完成时按实际等待天数（{0} 天）顺延项目", [d.elapsedDays || 0])}
                  </span>
                  <button type="button" onClick={markDone} disabled={!live.length || busy === "done"} data-testid={`dv3-ls-done-${stageKey}`}
                    title={live.length ? T("测绘报告已收到，标记完成并按实际交付天数顺延工期") : T("先上传测绘报告才能标记完成")}
                    className="ml-auto inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-40">
                    {busy === "done" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}{T("标记完成 · 报告已收到")}
                  </button>
                </>
              )}
              {!live.length && canWrite && <span className="text-[10px] text-red-600">{T("还缺：测绘报告")}</span>}
            </div>
          )}
          {state === "done" && (
            <p className="flex flex-wrap items-center gap-1 text-[10px] text-emerald-700" data-testid={`dv3-ls-doneline-${stageKey}`}>
              <CheckCircle2 className="h-3 w-3" />{T("{0} 标记完成", [d.doneBy])}{d.doneAt ? ` · ${fmtDT(d.doneAt)}` : ""}
              <span className="text-slate-500" data-testid={`dv3-ls-settled-${stageKey}`}>
                {T("· 实际 {0} 天，已按实际交付顺延 {1} 天", [d.settledDays ?? d.elapsedDays, d.shiftedDays || 0])}
              </span>
            </p>
          )}
        </div>
      )}
      {lb !== null && lbItems[lb] && (
        <MediaLightbox tid="dv3-ls-lightbox" items={lbItems} index={lb} onIndex={setLb} onClose={() => setLb(null)} api={api} zh />
      )}
    </div>
  );
};

export default LandSurveyBlock;
